import DateRangePicker from "components/common/DateRangePicker";
import SingleSelect from "components/common/SingleSelect";
import { useTranslation } from "react-i18next";
import {
  EVENT_CONFIG,
  OPERATOR_OPTIONS,
  TYPE_LABELS,
} from "utils/constants";

export const getEventDefault = (eventKey) => {
  const config = EVENT_CONFIG[eventKey];
  if (!config) return { operator: "EQUALS", value: "" };
  return { ...config.default };
};
export const getEventType = (eventKey) => {
  return EVENT_CONFIG[eventKey]?.type || "";
};
export const getEventOptions = (selectedEvents = []) =>
  Object.entries(EVENT_CONFIG)
    .filter(([value]) => !selectedEvents.includes(value))
    .map(([value, { label }]) => ({ name: label, value }));

const ConditionRuleInput = ({
  eventKey,
  value,
  onChange,
  operator,
  onOperatorChange,
  plansList = [],
  label = null,
  required = false,
  min = 0,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const config = EVENT_CONFIG[eventKey];

  if (!config) {
    return (
      <>
        {label && (
          <label className="common_label">
            {label}
            {required && <span className="text-danger">*</span>}
          </label>
        )}
        <div className="condition-rule d-flex align-items-center gap-2">
          <span className="text-muted small">{t("Select an event first")}</span>
        </div>
      </>
    );
  }

  const { type, operators, options } = config;
  const isBetween = operator === "BETWEEN";

  const filteredOperators = OPERATOR_OPTIONS.filter((opt) =>
    operators.includes(opt.value),
  );

  const planOptions = plansList.map((plan) => ({
    name: plan?.title?.en,
    value: plan?._id,
  }));

  return (
    <>
      {label && (
        <label className="common_label">
          {label}
          {required && <span className="text-danger">*</span>}
        </label>
      )}

      <div className="condition-rule d-flex align-items-center gap-2 flex-wrap">
        {/* ── Operator ── */}
        <SingleSelect
          options={[{ name: TYPE_LABELS[type], value: type }]}
          value={type}
          readOnlyInput={true}
          disabled={true}
          minWidth={120}
        />
        <SingleSelect
          options={filteredOperators}
          value={operator}
          readOnlyInput={true}
          disabled={true}
          handleSingleSelectChange={(opt) => {
            onOperatorChange(opt.value);
            if (opt.value === "BETWEEN") {
              onChange(type === "date" ? ["", ""] : [0, 0]);
            } else if (isBetween) {
              onChange(
                type === "date"
                  ? ""
                  : type === "number"
                    ? 0
                    : config.default.value,
              );
            }
          }}
          minWidth={180}
        />

        {/* ── Boolean ── */}
        {type === "boolean" && (
          <SingleSelect
            options={[
              { name: t("True"), value: true },
              { name: t("False"), value: false },
            ]}
            value={value}
            handleSingleSelectChange={(opt) => onChange(opt.value)}
            minWidth={120}
          />
        )}

        {/* ── Static select (membershipStatus) ── */}
        {type === "select" && (
          <SingleSelect
            options={options}
            value={value}
            handleSingleSelectChange={(opt) => onChange(opt.value)}
            minWidth={140}
          />
        )}

        {/* ── Plan dropdown (membershipPlanId) ── */}
        {type === "plan" && (
          <>
            <SingleSelect
              options={planOptions}
              value={value}
              handleSingleSelectChange={(opt) =>onChange(opt.value)}
              placeholder={t("Select Plan")}
              minWidth={200}
            />
          </>
        )}

        {/* ── Number single ── */}
        {type === "number" && !isBetween && (
          <input
            type="number"
            className="condition-input form-control"
            style={{ maxWidth: 100 }}
            value={value ?? 0}
            min={min}
            onChange={(e) => onChange(Number(e.target.value))}
          />
        )}

        {/* ── Number between ── */}
        {type === "number" && isBetween && (
          <>
            <input
              type="number"
              className="condition-input form-control"
              style={{ maxWidth: 100 }}
              value={value?.[0] ?? 0}
              min={min}
              placeholder={t("From")}
              onChange={(e) =>
                onChange([Number(e.target.value), value?.[1] ?? 0])
              }
            />
            <span className="text-muted">–</span>
            <input
              type="number"
              className="condition-input form-control"
              style={{ maxWidth: 100 }}
              value={value?.[1] ?? 0}
              min={min}
              placeholder={t("To")}
              onChange={(e) =>
                onChange([value?.[0] ?? 0, Number(e.target.value)])
              }
            />
          </>
        )}

        {/* ── Date single ── */}
        {type === "date" && !isBetween && (
          <DateRangePicker
            dates={value ?? ""}
            setDates={(val) => onChange(val)}
            range="single"
            placeholder="Select Date"
            showButtonBar={false}
          />
        )}

        {/* ── Date between ── */}
        {/* ── Date between ── */}
        {type === "date" && isBetween && (
          <DateRangePicker
            dates={value ?? ["", ""]}
            setDates={(val) => onChange(val)}
            range="range"
            placeholder="Select Date Range"
          />
        )}

        {/* ── String ── */}
        {type === "string" && (
          <input
            type="text"
            className="condition-input form-control"
            style={{ maxWidth: 200 }}
            value={value ?? ""}
            placeholder={t("Enter value")}
            onChange={(e) => onChange(e.target.value)}
          />
        )}
      </div>
    </>
  );
};

export default ConditionRuleInput;
